import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  manualSections as defaultSections,
  loadMarkdown as defaultLoadMarkdown,
  type ManualSection,
  type FlatPage,
} from './content';
import { replaceTerms, getGlossary } from './glossary';
import { brandConfig } from '../config/brands';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './ManualLayout.css';

export interface ManualPageProps {
  sections?: ManualSection[];
  basePath?: string;
  brandColorOverride?: string;
  sidebarTitle?: string;
  loadMarkdown?: (contentDir: string, slug: string) => Promise<string>;
  showFaq?: boolean;
  showContact?: boolean;
}

/** ReactMarkdown共通カスタムコンポーネント */
const markdownComponents = {
  h2: ({ children }: { children?: React.ReactNode }) => {
    const text = String(children).replace(/\*\*/g, '');
    const id = text.replace(/[(\)（）]/g, '').replace(/\s+/g, '-').toLowerCase();
    const handleCopyLink = (e: React.MouseEvent) => {
      e.preventDefault();
      // eslint-disable-next-line no-restricted-syntax -- pathname は現在 URL 由来で basename (/app) を既に含む
      const url = `${window.location.origin}${window.location.pathname}#${id}`;
      navigator.clipboard.writeText(url);
    };
    return (
      <h2 id={id} className="manual-h2-anchor">
        <a href={`#${id}`} className="manual-h2-anchor-link" onClick={handleCopyLink} title="リンクをコピー">
          #
        </a>
        {children}
      </h2>
    );
  },
  blockquote: ({ children }: { children?: React.ReactNode }) => {
    // childrenからテキストを再帰的に抽出して注意書きの種類を判定
    const extractText = (node: React.ReactNode): string => {
      if (node == null || typeof node === 'boolean') return '';
      if (typeof node === 'string' || typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(extractText).join('');
      if (typeof node === 'object' && 'props' in node) {
        return extractText((node as React.ReactElement<Record<string, unknown>>).props.children as React.ReactNode);
      }
      return '';
    };
    const textContent = extractText(children).trimStart();
    const calloutPatterns: [RegExp, string][] = [
      [/^注意/, 'warning'],
      [/^警告/, 'warning'],
      [/^ヒント/, 'tip'],
      [/^ポイント/, 'tip'],
      [/^補足/, 'info'],
      [/^参考/, 'info'],
    ];
    let type = '';
    for (const [pattern, cls] of calloutPatterns) {
      if (pattern.test(textContent)) {
        type = cls;
        break;
      }
    }
    if (type) {
      return <div className={`manual-callout manual-callout-${type}`}>{children}</div>;
    }
    return <blockquote>{children}</blockquote>;
  },
};

/** 記事コンテンツ:最初のh2の直前に目次を挿入 */
function ManualArticleContent({ content, toc, adminOnly }: { content: string; toc: { text: string; id: string }[]; adminOnly?: boolean }) {
  const components = useMemo(() => {
    if (!adminOnly) return markdownComponents;
    return {
      ...markdownComponents,
      h1: ({ children }: { children?: React.ReactNode }) => (
        <>
          <h1>{children}</h1>
          <span className="manual-admin-badge">管理者のみ</span>
        </>
      ),
    };
  }, [adminOnly]);
  const showToc = toc.length >= 2;

  if (!showToc) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    );
  }

  // 最初のh2の位置でMarkdownを分割
  const firstH2Match = content.match(/^## /m);
  const splitIndex = firstH2Match?.index ?? -1;
  const intro = splitIndex > 0 ? content.slice(0, splitIndex) : '';
  const body = splitIndex > 0 ? content.slice(splitIndex) : content;

  return (
    <>
      {intro && (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{intro}</ReactMarkdown>
      )}
      <nav className="manual-toc">
        <p className="manual-toc-heading">目次</p>
        <ul className="manual-toc-list">
          {toc.map(item => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="manual-toc-link"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >{item.text}</a>
            </li>
          ))}
        </ul>
      </nav>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {body}
      </ReactMarkdown>
    </>
  );
}

/** トップページに表示するFAQピックアップ */
const TOP_FAQ_ITEMS = [
  { question: '生成した画像や動画の著作権はどうなりますか？' },
  { question: '生成に失敗した場合、ポイントは消費されますか？' },
  { question: 'Nano Banana の「高品質 / 標準 / スピード」の違いは？' },
  { question: '無料で試すことはできますか？' },
  { question: '{{organization}}に他のメンバーを追加するには？' },
];

/** サイドバーナビゲーション */
function SidebarNav({
  resolvedSections,
  section,
  slug,
  openSections,
  toggleSection,
  glossary,
  onNavigate,
  basePath,
  sidebarTitle,
}: {
  resolvedSections: ReturnType<ManualSection[]['map']>;
  section?: string;
  slug?: string;
  openSections: Set<string>;
  toggleSection: (id: string) => void;
  glossary: Record<string, string>;
  onNavigate?: () => void;
  basePath: string;
  sidebarTitle: string;
}) {
  return (
    <>
      <div className="manual-sidebar-header">
        <Link to={basePath} className="manual-sidebar-title-link" onClick={onNavigate}>
          <img src={brandConfig.favicon ?? '/favicon.ico'} alt="" className="manual-sidebar-icon" />
          <h2 className="manual-sidebar-title">
            {replaceTerms(sidebarTitle, glossary)}
          </h2>
        </Link>
      </div>
      <nav className="manual-nav">
        {(resolvedSections as any[]).map((s: any) => {
          const isOpen = openSections.has(s.id);
          const hasActivePage = section === s.id;
          const isSinglePage = s.pages.length === 1;

          if (isSinglePage) {
            const p = s.pages[0];
            return (
              <div key={s.id} className="manual-nav-section">
                <Link
                  to={`${basePath}/${s.id}/${p.slug}`}
                  className={`manual-nav-section-title manual-nav-section-link ${hasActivePage ? 'has-active' : ''}`}
                  onClick={onNavigate}
                >
                  <span>{s.title}</span>
                </Link>
              </div>
            );
          }

          return (
            <div key={s.id} className="manual-nav-section">
              <button
                type="button"
                className={`manual-nav-section-title ${hasActivePage ? 'has-active' : ''}`}
                onClick={() => toggleSection(s.id)}
                aria-expanded={isOpen}
              >
                <span>{s.title}</span>
                <svg
                  className={`manual-nav-chevron ${isOpen ? 'open' : ''}`}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {isOpen && (
                <div className="manual-nav-pages">
                  {s.pages.map((p: any) => {
                    // 子ページは、親ページまたは兄弟の子ページが表示中の場合のみ表示
                    if (p.parentSlug && section === s.id && slug !== p.parentSlug) {
                      const currentPage = s.pages.find((pg: any) => pg.slug === slug);
                      if (!currentPage || currentPage.parentSlug !== p.parentSlug) {
                        return null;
                      }
                    }
                    return (
                      <Link
                        key={p.slug}
                        to={`${basePath}/${s.id}/${p.slug}`}
                        className={`manual-nav-link ${section === s.id && slug === p.slug ? 'active' : ''} ${p.parentSlug ? 'manual-nav-link-child' : ''}`}
                        onClick={onNavigate}
                      >
                        {p.title}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
}

function ManualTopPage({
  glossary,
  sections,
  basePath,
  sidebarTitle,
  showFaq,
  showContact,
}: {
  glossary: Record<string, string>;
  sections: ManualSection[];
  basePath: string;
  sidebarTitle: string;
  showFaq: boolean;
  showContact: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const resolvedSections = useMemo(() =>
    sections
      .filter(s => s.id !== 'faq')
      .map(s => ({
        ...s,
        title: replaceTerms(s.title, glossary),
        pages: s.pages.map(p => ({
          ...p,
          title: replaceTerms(p.title, glossary),
        })),
      })),
    [sections, glossary]
  );

  const faqItems = useMemo(() =>
    TOP_FAQ_ITEMS.map(item => ({
      ...item,
      question: replaceTerms(item.question, glossary),
    })),
    [glossary]
  );

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const results: { sectionId: string; slug: string; title: string; sectionTitle: string }[] = [];
    const allResolved = sections.map(s => ({
      ...s,
      title: replaceTerms(s.title, glossary),
      pages: s.pages.map(p => ({ ...p, title: replaceTerms(p.title, glossary) })),
    }));
    for (const s of allResolved) {
      for (const p of s.pages) {
        if (p.title.toLowerCase().includes(q)) {
          results.push({ sectionId: s.id, slug: p.slug, title: p.title, sectionTitle: s.title });
        }
      }
    }
    return results;
  }, [sections, glossary, searchQuery]);

  return (
    <div className="manual-top">
      <div className="manual-top-header">
        <h1 className="manual-top-title">
          {replaceTerms(sidebarTitle, glossary)}
        </h1>
        <div className="manual-top-search-wrapper">
          <input
            type="text"
            className="manual-top-search"
            placeholder="キーワードで検索..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}

          />
        </div>
      </div>

      {searchResults !== null ? (
        <div className="manual-top-results">
          {searchResults.length === 0 ? (
            <p className="manual-top-no-results">「{searchQuery}」に一致するページは見つかりませんでした。</p>
          ) : (
            <ul className="manual-top-results-list">
              {searchResults.map(r => (
                <li key={`${r.sectionId}-${r.slug}`}>
                  <Link to={`${basePath}/${r.sectionId}/${r.slug}`} className="manual-top-result-link">
                    <span className="manual-top-result-title">{r.title}</span>
                    <span className="manual-top-result-section">{r.sectionTitle}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <div className="manual-top-sections">
            {resolvedSections.map(s => (
              <Link
                key={s.id}
                to={`${basePath}/${s.id}/${s.pages[0].slug}`}
                className="manual-top-section-card"
              >
                <h3 className="manual-top-section-title">{s.title}</h3>
                <p className="manual-top-section-count">{s.pages.length}件の記事</p>
              </Link>
            ))}
          </div>

          {showFaq && (
            <div className="manual-top-faq">
              <h2 className="manual-top-faq-title">よくある質問</h2>
              <ul className="manual-top-faq-list">
                {faqItems.map((item, i) => (
                  <li key={i}>
                    <Link to={`${basePath}/faq/faq`} className="manual-top-faq-link">
                      {item.question}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to={`${basePath}/faq/faq`} className="manual-top-faq-more">
                すべてのよくある質問を見る →
              </Link>
            </div>
          )}

          {showContact && (
            <div className="manual-top-contact">
              <h2 className="manual-top-contact-title">お問い合わせ</h2>
              <p className="manual-top-contact-text">
                ヘルプで解決しない場合は、お気軽にお問い合わせください。
              </p>
              <a
                href={glossary.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="manual-top-contact-link"
              >
                お問い合わせフォーム →
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ManualPage({
  sections = defaultSections,
  basePath = '/manual',
  brandColorOverride,
  sidebarTitle = '{{serviceName}} ヘルプ',
  loadMarkdown = defaultLoadMarkdown,
  showFaq = true,
  showContact = true,
}: ManualPageProps = {}) {
  const { section, slug } = useParams<{ section?: string; slug?: string }>();
  const location = useLocation();
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const glossary = useMemo(() => getGlossary(), []);

  // sections に対する純関数(props 化に伴い内部で再構築)
  const findSection = useCallback((id: string) => sections.find(s => s.id === id), [sections]);
  const findPage = useCallback((sectionId: string, s: string) => findSection(sectionId)?.pages.find(p => p.slug === s), [findSection]);
  const getFlatPages = useCallback((): FlatPage[] => {
    const pages: FlatPage[] = [];
    for (const sec of sections) {
      for (const p of sec.pages) pages.push({ sectionId: sec.id, slug: p.slug, title: p.title });
    }
    return pages;
  }, [sections]);
  const getAdjacentPages = useCallback((sectionId: string, s: string) => {
    const flat = getFlatPages();
    const idx = flat.findIndex(p => p.sectionId === sectionId && p.slug === s);
    if (idx === -1) return { prev: null as FlatPage | null, next: null as FlatPage | null };
    return {
      prev: idx > 0 ? flat[idx - 1] : null,
      next: idx < flat.length - 1 ? flat[idx + 1] : null,
    };
  }, [getFlatPages]);

  const currentSection = section ? findSection(section) : undefined;
  const currentPage = section && slug ? findPage(section, slug) : undefined;
  const isTopPage = !section;
  const brandColor = brandColorOverride ?? glossary.brandColor ?? '#1a1a1a';

  // theme-colorメタタグを設定(Safariツールバー色)
  useEffect(() => {
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = brandColor;
  }, [brandColor]);

  const contentRef = useRef<HTMLElement>(null);

  // ページ遷移時にモバイルメニューを閉じ、スクロール処理
  useEffect(() => {
    setMobileMenuOpen(false);
    if (location.hash) {
      // ハッシュがある場合はコンテンツ読み込み後にスクロール
      const scrollToHash = () => {
        const id = decodeURIComponent(location.hash.slice(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };
      // コンテンツ描画後にリトライ
      if (!scrollToHash()) {
        const timer = setTimeout(scrollToHash, 200);
        return () => clearTimeout(timer);
      }
    } else {
      contentRef.current?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  // アコーディオン開閉状態
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (section) initial.add(section);
    return initial;
  });

  useEffect(() => {
    if (section) {
      setOpenSections(prev => new Set(prev).add(section));
    }
  }, [section]);

  const toggleSection = useCallback((sectionId: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (isTopPage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const load = async () => {
      const contentDir = currentSection?.contentDir ?? '';
      const raw = await loadMarkdown(contentDir, slug!);
      setMarkdown(raw);
      setLoading(false);
    };
    load();
  }, [section, slug, isTopPage, currentSection, loadMarkdown]);

  const renderedContentRaw = useMemo(() => replaceTerms(markdown, glossary), [markdown, glossary]);

  // 「関連ページ」「もっと詳しく」セクションを本文から分離
  const { mainContent: renderedContent, relatedLinks } = useMemo(() => {
    // basePath エスケープして正規表現に組み込む
    const escapedBase = basePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pathPattern = new RegExp(`^${escapedBase}\\/([^/]+)\\/([^/]+)$`);

    // パスからページタイトルを解決
    const resolveTitle = (href: string): string => {
      const match = href.match(pathPattern);
      if (!match) return href;
      const [, sectionId, pageSlug] = match;
      const sec = sections.find(s => s.id === sectionId);
      const page = sec?.pages.find(p => p.slug === pageSlug);
      return page ? replaceTerms(page.title, glossary) : href;
    };

    // リンクを抽出(Markdown形式 or パスのみ)
    const extractLinks = (text: string) => {
      const links: { title: string; href: string }[] = [];
      for (const line of text.split('\n')) {
        const trimmed = line.replace(/^[-*]\s*/, '').trim();
        if (!trimmed) continue;
        // [title](path) 形式
        const mdLink = trimmed.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (mdLink) {
          links.push({ title: mdLink[1], href: mdLink[2] });
          continue;
        }
        // パスのみ(basePath 付き、basePath なし、末尾/あり、先頭/なし すべて対応)
        let path = trimmed.replace(/\/+$/, ''); // 末尾の/を除去
        if (!path.startsWith('/')) path = '/' + path; // 先頭の/を補完
        if (!path.startsWith(`${basePath}/`)) path = `${basePath}${path}`; // basePath を補完
        if (path.match(pathPattern)) {
          links.push({ title: resolveTitle(path), href: path });
        }
      }
      return links;
    };

    const pattern = /\n---\s*\n+## (関連ページ|もっと詳しく)\s*\n([\s\S]*)$/;
    const m1 = renderedContentRaw.match(pattern);
    if (m1) {
      return { mainContent: renderedContentRaw.slice(0, m1.index!), relatedLinks: extractLinks(m1[2]) };
    }
    const pattern2 = /\n## (関連ページ|もっと詳しく)\s*\n([\s\S]*)$/;
    const m2 = renderedContentRaw.match(pattern2);
    if (m2) {
      return { mainContent: renderedContentRaw.slice(0, m2.index!), relatedLinks: extractLinks(m2[2]) };
    }
    return { mainContent: renderedContentRaw, relatedLinks: [] };
  }, [renderedContentRaw, glossary, basePath, sections]);

  // ページタイトルを設定。マニュアルはサフィックスも serviceName ではなく
  // サイドバータイトル (用語置換後) を使うため、suffix ごと override する。
  const resolvedSidebarTitle = useMemo(() => replaceTerms(sidebarTitle, glossary), [sidebarTitle, glossary]);
  const h1Title = useMemo(() => {
    if (isTopPage) return null;
    // Markdownからh1テキストを抽出
    return renderedContent.match(/^# (.+)$/m)?.[1] ?? null;
  }, [renderedContent, isTopPage]);
  useDocumentTitle(h1Title, { suffix: resolvedSidebarTitle });

  // h2見出しを抽出して目次を生成
  const toc = useMemo(() => {
    if (!renderedContent) return [];
    const matches = [...renderedContent.matchAll(/^## (.+)$/gm)];
    return matches.map(m => {
      const text = m[1].replace(/\*\*/g, '');
      const id = text.replace(/[(\)（）]/g, '').replace(/\s+/g, '-').toLowerCase();
      return { text, id };
    });
  }, [renderedContent]);

  const resolvedSections = useMemo(() =>
    sections.map(s => ({
      ...s,
      title: replaceTerms(s.title, glossary),
      pages: s.pages.map(p => ({
        ...p,
        title: replaceTerms(p.title, glossary),
      })),
    })),
    [sections, glossary]
  );

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  const adjacent = useMemo(() => {
    if (!section || !slug) return { prev: null, next: null };
    const { prev, next } = getAdjacentPages(section, slug);
    return {
      prev: prev ? { ...prev, title: replaceTerms(prev.title, glossary) } : null,
      next: next ? { ...next, title: replaceTerms(next.title, glossary) } : null,
    };
  }, [section, slug, glossary, getAdjacentPages]);

  return (
    <>
    <div className="manual-brand-bar" style={{ background: brandColor }} />
    <div className="manual-shell">
      {/* モバイル用ヘッダー */}
      <div className="manual-mobile-header">
        <button
          type="button"
          className="manual-mobile-menu-button"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label="メニュー"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <Link to={basePath} className="manual-mobile-title">
          {resolvedSidebarTitle}
        </Link>
      </div>

      {/* サイドバー(PC: 常時表示、モバイル: オーバーレイ) */}
      {mobileMenuOpen && (
        <div className="manual-sidebar-overlay" onClick={closeMobileMenu} />
      )}
      <aside className={`manual-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <SidebarNav
          resolvedSections={resolvedSections}
          section={section}
          slug={slug}
          openSections={openSections}
          toggleSection={toggleSection}
          glossary={glossary}
          onNavigate={closeMobileMenu}
          basePath={basePath}
          sidebarTitle={sidebarTitle}
        />
      </aside>

      {/* メインコンテンツ */}
      <main ref={contentRef} className="manual-content">
        {isTopPage ? (
          <ManualTopPage
            glossary={glossary}
            sections={sections}
            basePath={basePath}
            sidebarTitle={sidebarTitle}
            showFaq={showFaq}
            showContact={showContact}
          />
        ) : loading ? (
          <div className="manual-loading">読み込み中...</div>
        ) : (
          <>
            <article className="manual-article">
              <ManualArticleContent content={renderedContent} toc={toc} adminOnly={currentPage?.adminOnly} />
            </article>
            {(adjacent.prev || adjacent.next) && (
              <nav className="manual-page-nav">
                {adjacent.prev ? (
                  <Link to={`${basePath}/${adjacent.prev.sectionId}/${adjacent.prev.slug}`} className="manual-page-nav-link prev">
                    <span className="manual-page-nav-label">← 戻る</span>
                    <span className="manual-page-nav-title">{adjacent.prev.title}</span>
                  </Link>
                ) : <span />}
                {adjacent.next ? (
                  <Link to={`${basePath}/${adjacent.next.sectionId}/${adjacent.next.slug}`} className="manual-page-nav-link next">
                    <span className="manual-page-nav-label">次へ →</span>
                    <span className="manual-page-nav-title">{adjacent.next.title}</span>
                  </Link>
                ) : <span />}
              </nav>
            )}
          </>
        )}
      </main>

      {/* 右カラム: 関連ページ(PC のみ) */}
      {!isTopPage && relatedLinks.length > 0 && (
        <aside className="manual-related">
          <h3 className="manual-related-title">関連ページ</h3>
          <ul className="manual-related-list">
            {relatedLinks.map((link, i) => (
              <li key={i}>
                <Link to={link.href} className="manual-related-link">{link.title}</Link>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
    </>
  );
}
